import React, { useCallback, useMemo, useState } from 'react';
import { MdOutlineImportExport } from 'react-icons/md';
import { Button, GenericConfirmationModal, IconButton_IconSizes } from '@ydistri/ds';
import { useRunManualCalculationMutation } from './apiCalculations';
import { useTemplateOrCalculation } from '../../hooks/useTemplateOrCalculation';
import { CalculationStatus, CalculationType } from '@ydistri/api-sdk';
import { useGetCalculationQuery } from '../CalculationDetail/apiCalculationDetail';
import { useNavigate } from 'react-router';
import { RoutingPage } from '../../routes/routerSlice';
import { addToast } from '@ydistri/utils';
import { Popover } from 'antd';
import { ReduxState, useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import { RedistributionDisplayMode } from './calculationsSlice';
import { useStableParams } from '../../routes/useStableParams';

interface RecomputeStatisticsButtonProps {
  calculationStatus?: CalculationStatus;
  calculationType?: CalculationType;
  disabled?: boolean;
}

const importIcon = <MdOutlineImportExport size={IconButton_IconSizes.small} />;

const RecomputeStatisticsButton: React.FC<RecomputeStatisticsButtonProps> = ({
  calculationStatus,
  calculationType,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectShortName } = useStableParams();
  const redistributionDisplayMode = useSelector(
    (state: ReduxState) => state.calculations.redistributionDisplayMode,
  );

  const templateOrCalculation = useTemplateOrCalculation();
  const { data: calculationData } = useGetCalculationQuery(templateOrCalculation.id);
  const [runManualCalculation, { isLoading: sendingRunCommand }] =
    useRunManualCalculationMutation();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const onClickHandler = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const onClosed = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const onConfirmed = useCallback(() => {
    if (templateOrCalculation.type === 'Calculation') {
      const createErrorToast = () =>
        dispatch(
          addToast({
            message: `There was an error when trying to recompute statistics`,
            isError: true,
          }),
        );

      runManualCalculation(templateOrCalculation.id)
        .unwrap()
        .then(() => {
          dispatch(addToast({ message: 'Statistics recomputation will start shortly' }));
          navigate(`/${projectShortName}/${RoutingPage.CALCULATIONS}`);
        })
        .catch(() => {
          createErrorToast();
        });
    }
  }, [
    dispatch,
    navigate,
    projectShortName,
    runManualCalculation,
    templateOrCalculation.id,
    templateOrCalculation.type,
  ]);

  if (calculationStatus === CalculationStatus.Running) {
    disabled = true;
  }

  const loading = sendingRunCommand || calculationData?.status === CalculationStatus.Running;
  const isDisabled =
    loading ||
    disabled ||
    calculationType === CalculationType.Production ||
    redistributionDisplayMode === RedistributionDisplayMode.IMPORT;

  const button = useMemo(
    () => (
      <Button
        $colorVariant={isDisabled ? 'ghost' : 'primary'}
        $textVariant="semibold"
        icon={importIcon}
        type="primary"
        onClick={onClickHandler}
        loading={loading}
        disabled={isDisabled}
      >
        Recompute statistics...
      </Button>
    ),
    [isDisabled, loading, onClickHandler],
  );

  if (calculationType === CalculationType.Production) {
    return (
      <Popover content="Production calculations can't be recomputed" placement="left">
        {button}
      </Popover>
    );
  } else {
    return (
      <>
        {showConfirmation && (
          <GenericConfirmationModal
            title="Recompute statistics"
            onConfirmed={onConfirmed}
            onCanceled={onClosed}
            message="The statistics will be recomputed. This might take a while. Do you want to continue?"
          />
        )}
        {button}
      </>
    );
  }
};

export default RecomputeStatisticsButton;
